import type { Settings } from '#/global'
import apiUser from '@/api/modules/user'
import router from '@/router'
import settingsDefault from '@/settings'
import eventBus from '@/utils/eventBus'
import { diffTwoObj, mergeWithoutUndefinedProps } from '@/utils/object'
import storage from '@/utils/storage'
import { cloneDeep } from 'es-toolkit'
import useMenuStore from './menu'
import useRouteStore from './route'
import useSettingsStore from './settings'
import useTabbarStore from './tabbar'

const useUserStore = defineStore(
  // 唯一ID
  'user',
  () => {
    const settingsStore = useSettingsStore()
    const tabbarStore = useTabbarStore()
    const routeStore = useRouteStore()
    const menuStore = useMenuStore()

    const account = ref(storage.local.get('account') ?? '')
    const token = ref(storage.local.get('token') ?? '')
    const avatar = ref(storage.local.get('avatar') ?? '')
    const tenantId = ref(storage.local.get('tenantId') ?? '')
    const profile = ref<any>({})
    const permissions = ref<string[]>([])
    const allowStudio = ref(storage.local.get('allowStudio') ?? false)
    const isLogin = computed(() => {
      if (token.value) {
        return true
      }
      return false
    })
    const version = ref(storage.local.get('version') ?? '')
    const versionName = ref(storage.local.get('versionName') ?? '')
    const expirationTime = ref(storage.local.get('expirationTime') ?? '')
    const domain = ref(storage.local.get('domain') ?? '')
    async function editPassword(data: {
      password: string
      newPassword: string
      checkPassword: string
    }) {
      const res = await apiUser.profileEdit(data)
      profile.value = res.data
      return res.data
    }

    // 登录
    async function login(data: {
      account: string
      password: string
    }) {
      const res = await apiUser.login(data)
      storage.local.set('account', res.data.account)
      storage.local.set('id', res.data.id)
      storage.local.set('token', res.data.token)
      storage.local.set('avatar', res.data.avatar)
      storage.local.set('tenantId', res.data.tenantId)
      storage.local.set('version', res.data.version?.versionId)
      storage.local.set('versionName', res.data.version?.versionName)
      storage.local.set('expirationTime', res.data.version?.endDate)
      storage.local.set('domain', res.data.version?.domain)
      tenantId.value = res.data.tenantId
      account.value = res.data.account
      token.value = res.data.token
      avatar.value = res.data.avatar
      version.value = res.data.version?.versionId
      versionName.value = res.data.version?.versionName
      expirationTime.value = res.data.version?.endDate
      domain.value = res.data.version?.domain
    }

    // 一键登录
    async function onekeylogin(data: {
      account: string
      avatar: string
      id: string
      token: string
      tenantId: string
      versionId: string
      versionName: string
      endDate: string
      domain: string
    }) {
      const versionIdStr = String(data.versionId).trim()
      storage.local.set('account', data.account)
      storage.local.set('id', data.id)
      storage.local.set('token', data.token)
      storage.local.set('avatar', data.avatar)
      storage.local.set('tenantId', data.tenantId)
      storage.local.set('version', versionIdStr)
      storage.local.set('versionName', data.versionName)
      storage.local.set('expirationTime', data.endDate)
      storage.local.set('domain', data.domain)
      tenantId.value = data.tenantId
      account.value = data.account
      token.value = data.token
      avatar.value = data.avatar
      version.value = versionIdStr
      versionName.value = data.versionName
      expirationTime.value = data.endDate
      domain.value = data.domain
      return Promise.resolve(true)
    }
    // 手动登出
    function logout(redirect = router.currentRoute.value.fullPath) {
      // 此处仅清除计算属性 isLogin 中判断登录状态过期的变量，以保证在弹出登录窗口模式下页面展示依旧正常
      storage.local.remove('token')
      token.value = ''
      router.push({
        name: 'login',
        query: {
          ...(redirect !== settingsStore.settings.home.fullPath && router.currentRoute.value.name !== 'login' && { redirect }),
        },
      }).then(logoutCleanStatus)
    }
    // 请求登出
    function requestLogout() {
      // 此处仅清除计算属性 isLogin 中判断登录状态过期的变量，以保证在弹出登录窗口模式下页面展示依旧正常
      storage.local.remove('token')
      token.value = ''
      if (settingsStore.settings.app.loginExpiredMode === 'redirect') {
        router.push({
          name: 'login',
          query: {
            ...(
              router.currentRoute.value.fullPath !== settingsStore.settings.home.fullPath
              && router.currentRoute.value.name !== 'login'
              && {
                redirect: router.currentRoute.value.fullPath,
              }
            ),
          },
        }).then(logoutCleanStatus)
      }
      else {
        eventBus.emit('global-login-again-visible')
      }
    }
    // 登出后清除状态
    function logoutCleanStatus() {
      // 保存当前语言设置
      const currentLang = localStorage.getItem('app_language');
      storage.local.clear();
      // 恢复语言设置
      if (currentLang) {
        localStorage.setItem('app_language', currentLang);
      }
      tenantId.value = ''
      account.value = ''
      avatar.value = ''
      permissions.value = []
      allowStudio.value = false
      settingsStore.updateSettings({}, true)
      tabbarStore.clean()
      routeStore.removeRoutes()
      menuStore.setActived(0)
      // 动态获取最新语言设置
      const latestLang = localStorage.getItem('app_language');
      settingsStore.setDefaultLang(latestLang || 'zh-cn')
      version.value = ''
      versionName.value = ''
      expirationTime.value = ''
      domain.value = ''
    }

    // 获取权限
    async function getPermissions() {
      const res = await apiUser.permission()
      permissions.value = res.data
      await getTenantStudio()
    }
    // 获取权限
    async function getTenantStudio() {
      const res = await apiUser.getTenantStudio()
      allowStudio.value = res.data
      storage.local.set('allowStudio', res.data)
    }
    // 获取用户配置
    async function getProfile() {
      const res = await apiUser.profile()
      profile.value = res.data
      return res.data
    }

    // 框架已将可提供给用户配置的选项提取出来，请勿新增其他选项，不需要的选项可以在这里注释掉
    const preferences = ref<Settings.all>({
      app: {
        themeSync: settingsDefault.app.themeSync,
        colorScheme: settingsDefault.app.colorScheme,
        lightTheme: settingsDefault.app.lightTheme,
        darkTheme: settingsDefault.app.darkTheme,
        radius: settingsDefault.app.radius,
        enableColorAmblyopiaMode: settingsDefault.app.enableColorAmblyopiaMode,
        enableProgress: settingsDefault.app.enableProgress,
        defaultLang: settingsDefault.app.defaultLang,
      },
      menu: {
        mode: settingsDefault.menu.mode,
        style: settingsDefault.menu.style,
        mainMenuClickMode: settingsDefault.menu.mainMenuClickMode,
        subMenuUniqueOpened: settingsDefault.menu.subMenuUniqueOpened,
        subMenuCollapse: settingsDefault.menu.subMenuCollapse,
        subMenuAutoCollapse: settingsDefault.menu.subMenuAutoCollapse,
        enableSubMenuCollapseButton: settingsDefault.menu.enableSubMenuCollapseButton,
      },
      layout: {
        widthMode: settingsDefault.layout.widthMode,
      },
      mainPage: {
        enableTransition: settingsDefault.mainPage.enableTransition,
        transitionMode: settingsDefault.mainPage.transitionMode,
      },
      topbar: {
        mode: settingsDefault.topbar.mode,
        switchTabbarAndToolbar: settingsDefault.topbar.switchTabbarAndToolbar,
      },
      tabbar: {
        style: settingsDefault.tabbar.style,
        enableIcon: settingsDefault.tabbar.enableIcon,
        dblclickAction: settingsDefault.tabbar.dblclickAction,
        enableMemory: settingsDefault.tabbar.enableMemory,
      },
      toolbar: {
        breadcrumb: settingsDefault.toolbar.breadcrumb,
        navSearch: settingsDefault.toolbar.navSearch,
        fullscreen: settingsDefault.toolbar.fullscreen,
        pageReload: settingsDefault.toolbar.pageReload,
        colorScheme: settingsDefault.toolbar.colorScheme,
        layout: settingsDefault.toolbar.layout,
      },
      breadcrumb: {
        style: settingsDefault.breadcrumb.style,
        enableMainMenu: settingsDefault.breadcrumb.enableMainMenu,
      },
    })
    // isPreferencesUpdating 用于防止循环更新
    let isPreferencesUpdating = false
    watch(() => settingsStore.settings, (val) => {
      if (!settingsStore.settings.userPreferences.enable) {
        return
      }
      if (!isPreferencesUpdating) {
        isPreferencesUpdating = true
        preferences.value = mergeWithoutUndefinedProps(val, preferences.value)
      }
      else {
        isPreferencesUpdating = false
      }
    }, {
      deep: true,
    })
    watch(preferences, (val) => {
      if (!settingsStore.settings.userPreferences.enable) {
        return
      }
      if (!isPreferencesUpdating) {
        isPreferencesUpdating = true
        settingsStore.updateSettings(cloneDeep(val))
      }
      else {
        isPreferencesUpdating = false
      }
      updatePreferences(cloneDeep(val))
    }, {
      deep: true,
    })
    // isPreferencesInited 用于防止初始化时触发更新
    let isPreferencesInited = false
    // 获取偏好设置
    async function getPreferences() {
      let data: Settings.all = {}
      if (settingsStore.settings.userPreferences.storageTo === 'local') {
        if (storage.local.has('userPreferences')) {
          data = JSON.parse(storage.local.get('userPreferences') as string)[account.value] || {}
        }
      }
      else if (settingsStore.settings.userPreferences.storageTo === 'server') {
        const res = await apiUser.preferences()
        data = JSON.parse(res.data.preferences || '{}') as Settings.all
      }
      preferences.value = mergeWithoutUndefinedProps(data, preferences.value)
    }
    // 更新偏好设置
    async function updatePreferences(data: Settings.all = {}) {
      if (!isPreferencesInited) {
        isPreferencesInited = true
        return
      }
      if (!isLogin.value) {
        return
      }
      data = diffTwoObj(settingsDefault, data)
      if (settingsStore.settings.userPreferences.storageTo === 'local') {
        const userPreferencesData = storage.local.has('userPreferences') ? JSON.parse(storage.local.get('userPreferences') as string) : {}
        userPreferencesData[account.value] = data
        storage.local.set('userPreferences', JSON.stringify(userPreferencesData))
      }
      else if (settingsStore.settings.userPreferences.storageTo === 'server') {
        await apiUser.preferencesEdit(JSON.stringify(data))
      }
    }

    return {
      account,
      token,
      avatar,
      permissions,
      isLogin,
      tenantId,
      version,
      versionName,
      expirationTime,
      login,
      logout,
      requestLogout,
      getPermissions,
      preferences,
      getPreferences,
      updatePreferences,
      getProfile,
      editPassword,
      onekeylogin,
      allowStudio,
    }
  },
)

export default useUserStore
